/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';

import { checkToken } from 'api/utils';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import {
  autorizarFactorSbc,
  deleteFactorSbc,
  getFactoresSbc,
  getFactorSbc,
  patchFactorSbc,
  postFactorSbc,
} from 'api/catalogos/FactorSBC';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';

export const niveldeestudioPermission = {
  permissionModel: 'factordeintegracionimss',
  catalogsModels: [],
  tabsModels: [],
};

const FactorSBC = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [tiposDeNominaEmpleado, setTiposDeNominaEmpleado] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getFactoresSbc();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tipos_de_nominas_empleados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeNominasEmpleados(),
        getFactoresSbc(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setTiposDeNominaEmpleado(_tipos_de_nominas_empleados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _nivelDeEstudio = await getFactorSbc(match.id);
    setTimeout(() => {
      if (_nivelDeEstudio) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_nivelDeEstudio));
          setDisableFields(_nivelDeEstudio?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };
  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();

      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      // Formatear fechas al formato que requiere la API
      if (values.vigencia_inicial && values.vigencia_inicial.format) {
        values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      }

      if (values.vigencia_final && values.vigencia_final.format) {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      }
      // eslint-disable-next-line max-len
      const saved = await (key ? patchFactorSbc(key, values) : postFactorSbc(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteFactorSbc(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Factor SBC ya autorizado');
        return;
      }
      const saved = await autorizarFactorSbc(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un Factor SBC ya cancelado');
        return;
      }
      const saved = await patchFactorSbc(key, { estados_globales: ESTATUS_CANCELADO });
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede reactivar un Factor SBC ya activo');
        return;
      }
      const saved = await patchFactorSbc(key, { estados_globales: ESTATUS_ACTIVO });
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipos_de_nomina_empleado',
      key: 'tipos_de_nomina_empleado',
      width: 200,
      render: (val) => val?.map((id) => tiposDeNominaEmpleado.find((e) => e.id === id)?.descripcion).join(', ') || 'Sin datos',
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 100,
      render: (val) => (val ? val?.format(DATE_FRONTEND_FORMAT) : 'N/A'),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 100,
      render: (val) => (val ? val?.format(DATE_FRONTEND_FORMAT) : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'factordeintegracionimss', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_AUTORIZADO
                || selectedData?.estados_globales === ESTATUS_CANCELADO,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: 'factordeintegracionimss', action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: true,
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_CANCELADO,
              className: 'cancel',
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: 'factordeintegracionimss', action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: true,
              disabled: !selectedRowKeys?.length
                || selectedData?.estados_globales === ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Factor SBC`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
              aplica_factor_de_sbc_fijo: false,

            }}
          >
            <Row gutter={[16, 16]}>
              {/* Tipo de Nómina Empleado */}
              <Col span={24}>
                <Form.Item
                  name="tipos_de_nomina_empleado"
                  label="Tipo de Nómina Empleado"
                >
                  <Select
                    dataSource={tiposDeNominaEmpleado}
                    keyProp="id"
                    labelProp="descripcion"
                    allowClear
                    mode="multiple"
                  />
                </Form.Item>

              </Col>

              {/* Aplica Factor de SBC Fijo */}
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="aplica_factor_de_sbc_fijo"
                  label="Aplica Factor de SBC Fijo"
                  rules={[{ required: true, message: 'Seleccione si aplica factor fijo' }]}
                  hasFeedback
                >
                  <Select
                    trueFalse
                  />
                </Form.Item>
              </Col>

              {/* Factor de SBC */}
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => (
                    prev.aplica_factor_de_sbc_fijo !== curr.aplica_factor_de_sbc_fijo
                  )}
                >
                  {({ getFieldValue }) => {
                    const aplica = getFieldValue('aplica_factor_de_sbc_fijo'); // true / false
                    return (
                      <Form.Item
                        name="factor_de_sbc"
                        label="Factor de SBC"
                        rules={[
                          aplica
                            ? { required: true, message: 'Ingrese el factor SBC' }
                            : {}, // si es false, no requerido
                          { pattern: /^\d+(\.\d{1,5})?$/, message: 'Máximo 5 decimales' },
                        ]}
                        hasFeedback
                      >
                        <Input
                          type="number"
                          step="0.00001"
                          disabled={disableFields || !aplica}
                          placeholder="Ej. 1.15507"
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              {/* Vigencia Inicial */}
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_inicial"
                  label="Vigencia Inicial"
                  rules={[{ required: true, message: 'Seleccione la vigencia inicial' }]}
                  hasFeedback
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Vigencia Final */}
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                    onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`¿Desea eliminar el Factor SBC del tipo de nómina "${tiposDeNominaEmpleado.find(
            (t) => selectedData?.tipos_de_nomina_empleado?.includes(t.id),
          )?.descripcion || 'Sin descripción'}"?`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`autorizar el Factor SBC del tipo de nómina "${tiposDeNominaEmpleado.find(
            (t) => selectedData?.tipos_de_nomina_empleado?.includes(t.id),
          )?.descripcion || 'Sin descripción'}"`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`cancelar el Factor SBC del tipo de nómina "${tiposDeNominaEmpleado.find(
            (t) => selectedData?.tipos_de_nomina_empleado?.includes(t.id),
          )?.descripcion || 'Sin descripción'}"`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`reactivar el Factor SBC del tipo de nómina "${tiposDeNominaEmpleado.find(
            (t) => selectedData?.tipos_de_nomina_empleado?.includes(t.id),
          )?.descripcion || 'Sin descripción'}"`}
          open={visibleReactivar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default FactorSBC;
